import { Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { AppLayout } from "@/components/layout";
import { MeLayout } from "@/components/me-shell/me-layout";
import { ProtectedRoute } from "@/components/protected-route";
import {
  RequiresAiAssistant,
  RequiresModule,
} from "@/components/requires-module";
import { RequiresRbacManager } from "@/components/requires-rbac-manager";
import { RequiresSuperAdmin } from "@/components/requires-super-admin";
import { Skeleton } from "@/components/ui/skeleton";
import { ModuleCode } from "@/lib/me";
import { lazyWithReload as lazy } from "@/lib/lazy-with-reload";

const KioskPage = lazy(() =>
  import("@/features/biometrics-kiosk/KioskPage").then((m) => ({
    default: m.KioskPage,
  })),
);
const BiometricReviewQueuePage = lazy(() =>
  import("@/features/biometrics-review/ReviewQueuePage").then((m) => ({
    default: m.ReviewQueuePage,
  })),
);
const BiometricEnrollmentWizardPage = lazy(() =>
  import("@/features/biometrics-enrollment/EnrollmentWizardPage").then((m) => ({
    default: m.EnrollmentWizardPage,
  })),
);
const BiometricEnrollmentReviewPage = lazy(() =>
  import("@/features/biometrics-enrollment-review/EnrollmentReviewQueuePage").then(
    (m) => ({ default: m.EnrollmentReviewQueuePage }),
  ),
);
const BiometricRegisterDevicePage = lazy(() =>
  import("@/features/biometrics-devices/RegisterDevicePage").then((m) => ({
    default: m.RegisterDevicePage,
  })),
);
const BiometricDevicesPage = lazy(() =>
  import("@/features/biometrics-devices/DevicesPage").then((m) => ({
    default: m.DevicesPage,
  })),
);
const BiometricProviderSettingsPage = lazy(() =>
  import("@/features/biometrics-settings/ProviderSettingsPage").then((m) => ({
    default: m.ProviderSettingsPage,
  })),
);
const MeCheckInPage = lazy(() =>
  import("@/routes/me/check-in").then((m) => ({ default: m.MeCheckInPage })),
);

// Auth + 404 are eager — small, and the entry into the app should be instant.
import { LoginPage } from "@/routes/login";
import { NotFoundPage } from "@/routes/not-found";

// Workspace + Insights — high traffic, eager so the hub feels snappy.
import { DashboardPage } from "@/routes/dashboard";
import { EmployeesPage } from "@/routes/employees";

// The rest are code-split. Each route ships in its own chunk that
// only loads when navigated to. Cuts the initial bundle considerably.
const EmployeeDetailPage = lazy(() =>
  import("@/routes/employee-detail").then((m) => ({
    default: m.EmployeeDetailPage,
  })),
);
const LeaveApplicationsPage = lazy(() =>
  import("@/routes/leave-applications").then((m) => ({
    default: m.LeaveApplicationsPage,
  })),
);
const ExpensesPage = lazy(() =>
  import("@/routes/expenses").then((m) => ({ default: m.ExpensesPage })),
);
const PayrollRunsPage = lazy(() =>
  import("@/routes/payroll-runs").then((m) => ({ default: m.PayrollRunsPage })),
);
const PayrollSettingsPage = lazy(() =>
  import("@/routes/payroll-settings").then((m) => ({
    default: m.PayrollSettingsPage,
  })),
);
const SalarySlipsPage = lazy(() =>
  import("@/routes/salary-slips").then((m) => ({ default: m.SalarySlipsPage })),
);
const SalarySlipDetailPage = lazy(() =>
  import("@/routes/salary-slip-detail").then((m) => ({
    default: m.SalarySlipDetailPage,
  })),
);
const JobOpeningsPage = lazy(() =>
  import("@/routes/job-openings").then((m) => ({ default: m.JobOpeningsPage })),
);
const ApplicantPipelinePage = lazy(() =>
  import("@/routes/applicant-pipeline").then((m) => ({
    default: m.ApplicantPipelinePage,
  })),
);
const JobOpeningDetailPage = lazy(() =>
  import("@/routes/job-opening-detail").then((m) => ({
    default: m.JobOpeningDetailPage,
  })),
);
const JobApplicantDetailPage = lazy(() =>
  import("@/routes/job-applicant-detail").then((m) => ({
    default: m.JobApplicantDetailPage,
  })),
);
const AppraisalCyclesPage = lazy(() =>
  import("@/routes/appraisal-cycles").then((m) => ({
    default: m.AppraisalCyclesPage,
  })),
);
const AppraisalCycleDetailPage = lazy(() =>
  import("@/routes/appraisal-cycle-detail").then((m) => ({
    default: m.AppraisalCycleDetailPage,
  })),
);
const GoalsPage = lazy(() =>
  import("@/routes/goals").then((m) => ({ default: m.GoalsPage })),
);
const ReportsPage = lazy(() =>
  import("@/routes/reports").then((m) => ({ default: m.ReportsPage })),
);
const AttendancePage = lazy(() =>
  import("@/routes/attendance").then((m) => ({ default: m.AttendancePage })),
);
const CalendarPage = lazy(() =>
  import("@/routes/calendar").then((m) => ({ default: m.CalendarPage })),
);
const ShiftsPage = lazy(() =>
  import("@/routes/shifts").then((m) => ({ default: m.ShiftsPage })),
);
const ApprovalsPage = lazy(() =>
  import("@/routes/approvals").then((m) => ({ default: m.ApprovalsPage })),
);
const PhCompliancePage = lazy(() =>
  import("@/routes/ph-compliance").then((m) => ({
    default: m.PhCompliancePage,
  })),
);
const LifecyclePage = lazy(() =>
  import("@/routes/lifecycle").then((m) => ({ default: m.LifecyclePage })),
);
const SeparationDetailPage = lazy(() =>
  import("@/routes/separation-detail").then((m) => ({
    default: m.SeparationDetailPage,
  })),
);
const PromotionDetailPage = lazy(() =>
  import("@/routes/promotion-detail").then((m) => ({
    default: m.PromotionDetailPage,
  })),
);
const TransferDetailPage = lazy(() =>
  import("@/routes/transfer-detail").then((m) => ({
    default: m.TransferDetailPage,
  })),
);
const GrievanceDetailPage = lazy(() =>
  import("@/routes/grievance-detail").then((m) => ({
    default: m.GrievanceDetailPage,
  })),
);
const OnboardingDetailPage = lazy(() =>
  import("@/routes/onboarding-detail").then((m) => ({
    default: m.OnboardingDetailPage,
  })),
);
const FinalPayDetailPage = lazy(() =>
  import("@/routes/final-pay-detail").then((m) => ({
    default: m.FinalPayDetailPage,
  })),
);
const ExitInterviewDetailPage = lazy(() =>
  import("@/routes/exit-interview-detail").then((m) => ({
    default: m.ExitInterviewDetailPage,
  })),
);
const AccountingPage = lazy(() =>
  import("@/routes/accounting").then((m) => ({
    default: m.AccountingPage,
  })),
);
const JournalEntryNewPage = lazy(() =>
  import("@/routes/journal-entry-new").then((m) => ({
    default: m.JournalEntryNewPage,
  })),
);
const AccountingReportsPage = lazy(() =>
  import("@/routes/accounting-reports").then((m) => ({
    default: m.AccountingReportsPage,
  })),
);
const JournalEntryDetailPage = lazy(() =>
  import("@/routes/journal-entry-detail").then((m) => ({
    default: m.JournalEntryDetailPage,
  })),
);
const PaymentEntryNewPage = lazy(() =>
  import("@/routes/payment-entry-new").then((m) => ({
    default: m.PaymentEntryNewPage,
  })),
);
const PaymentEntryDetailPage = lazy(() =>
  import("@/routes/payment-entry-detail").then((m) => ({
    default: m.PaymentEntryDetailPage,
  })),
);
const AccountDetailPage = lazy(() =>
  import("@/routes/account-detail").then((m) => ({
    default: m.AccountDetailPage,
  })),
);
const FiscalYearDetailPage = lazy(() =>
  import("@/routes/fiscal-year-detail").then((m) => ({
    default: m.FiscalYearDetailPage,
  })),
);
const CostCenterDetailPage = lazy(() =>
  import("@/routes/cost-center-detail").then((m) => ({
    default: m.CostCenterDetailPage,
  })),
);
const CompensationPage = lazy(() =>
  import("@/routes/compensation").then((m) => ({
    default: m.CompensationPage,
  })),
);
const SalaryStructureDetailPage = lazy(() =>
  import("@/routes/compensation-structure-detail").then((m) => ({
    default: m.SalaryStructureDetailPage,
  })),
);
const WorkflowDefinitionDetailPage = lazy(() =>
  import("@/routes/workflow-definition-detail").then((m) => ({
    default: m.WorkflowDefinitionDetailPage,
  })),
);
const AdHocQueryDetailPage = lazy(() =>
  import("@/routes/ad-hoc-query-detail").then((m) => ({
    default: m.AdHocQueryDetailPage,
  })),
);
const WorkflowAdminPage = lazy(() =>
  import("@/routes/workflow-admin").then((m) => ({
    default: m.WorkflowAdminPage,
  })),
);
const OrgSettingsPage = lazy(() =>
  import("@/routes/org-settings").then((m) => ({
    default: m.OrgSettingsPage,
  })),
);
const ReportBuilderPage = lazy(() =>
  import("@/routes/report-builder").then((m) => ({
    default: m.ReportBuilderPage,
  })),
);
const StatutoryExportsPage = lazy(() =>
  import("@/routes/statutory-exports").then((m) => ({
    default: m.StatutoryExportsPage,
  })),
);
const NotificationsPage = lazy(() =>
  import("@/routes/notifications").then((m) => ({
    default: m.NotificationsPage,
  })),
);
const SettingsPage = lazy(() =>
  import("@/routes/settings").then((m) => ({ default: m.SettingsPage })),
);
const SettingsNotificationsPage = lazy(() =>
  import("@/routes/settings-notifications").then((m) => ({
    default: m.SettingsNotificationsPage,
  })),
);
const SettingsProfilePage = lazy(() =>
  import("@/routes/settings-profile").then((m) => ({
    default: m.SettingsProfilePage,
  })),
);
const SettingsRolesPage = lazy(() =>
  import("@/routes/settings-roles").then((m) => ({
    default: m.SettingsRolesPage,
  })),
);
const SettingsNumberSeriesPage = lazy(() =>
  import("@/routes/settings-number-series").then((m) => ({
    default: m.SettingsNumberSeriesPage,
  })),
);

// Vendor control plane (super-admin only).
const AdminClientsPage = lazy(() =>
  import("@/routes/admin/clients").then((m) => ({ default: m.AdminClientsPage })),
);
const AdminClientDetailPage = lazy(() =>
  import("@/routes/admin/client-detail").then((m) => ({
    default: m.AdminClientDetailPage,
  })),
);
const AdminPlansPage = lazy(() =>
  import("@/routes/admin/plans").then((m) => ({ default: m.AdminPlansPage })),
);

// Self-service (§9 PWA) — separate shell, separate chunks.
const MeHomePage = lazy(() =>
  import("@/routes/me/home").then((m) => ({ default: m.MeHomePage })),
);
const MeLeavePage = lazy(() =>
  import("@/routes/me/leave").then((m) => ({ default: m.MeLeavePage })),
);
const MeAssistantPage = lazy(() =>
  import("@/routes/me/assistant").then((m) => ({ default: m.MeAssistantPage })),
);
const MePayslipsPage = lazy(() =>
  import("@/routes/me/payslips").then((m) => ({ default: m.MePayslipsPage })),
);
const MeAttendancePage = lazy(() =>
  import("@/routes/me/attendance").then((m) => ({
    default: m.MeAttendancePage,
  })),
);
const MeProfilePage = lazy(() =>
  import("@/routes/me/profile").then((m) => ({ default: m.MeProfilePage })),
);

function RouteFallback() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-live="polite">
      <Skeleton className="h-9 w-1/3" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-72 w-full" />
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* Kiosk — unauthenticated route (device-token auth only).
          Module gating still applies: rendering the kiosk for a
          company that hasn't enabled the module would be confusing. */}
      <Route
        path="/kiosk"
        element={
          <Suspense fallback={<RouteFallback />}>
            <KioskPage />
          </Suspense>
        }
      />

      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="employees" element={<EmployeesPage />} />

        {/* Biometrics module — all surfaces gated by <RequiresModule>.
            When the tenant has biometric_attendance disabled the
            gate redirects to /; combined with the sidebar's module
            filter this keeps disabled modules invisible end-to-end. */}
        <Route
          path="biometrics/review-queue"
          element={
            <RequiresModule code={ModuleCode.BIOMETRIC_ATTENDANCE}>
              <Suspense fallback={<RouteFallback />}>
                <BiometricReviewQueuePage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="biometrics/enrollments/new"
          element={
            <RequiresModule code={ModuleCode.BIOMETRIC_ATTENDANCE}>
              <Suspense fallback={<RouteFallback />}>
                <BiometricEnrollmentWizardPage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="biometrics/enrollment-review"
          element={
            <RequiresModule code={ModuleCode.BIOMETRIC_ATTENDANCE}>
              <Suspense fallback={<RouteFallback />}>
                <BiometricEnrollmentReviewPage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="biometrics/devices/register"
          element={
            <RequiresModule code={ModuleCode.BIOMETRIC_ATTENDANCE}>
              <Suspense fallback={<RouteFallback />}>
                <BiometricRegisterDevicePage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="biometrics/devices"
          element={
            <RequiresModule code={ModuleCode.BIOMETRIC_ATTENDANCE}>
              <Suspense fallback={<RouteFallback />}>
                <BiometricDevicesPage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="biometrics/settings"
          element={
            <RequiresModule code={ModuleCode.BIOMETRIC_ATTENDANCE}>
              <Suspense fallback={<RouteFallback />}>
                <BiometricProviderSettingsPage />
              </Suspense>
            </RequiresModule>
          }
        />

        <Route
          path="employees/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <EmployeeDetailPage />
            </Suspense>
          }
        />
        <Route
          path="leave-applications"
          element={
            <Suspense fallback={<RouteFallback />}>
              <LeaveApplicationsPage />
            </Suspense>
          }
        />
        <Route
          path="expenses"
          element={
            <RequiresModule code={ModuleCode.EXPENSES}>
              <Suspense fallback={<RouteFallback />}>
                <ExpensesPage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="payroll-runs"
          element={
            <RequiresModule code={ModuleCode.PAYROLL}>
              <Suspense fallback={<RouteFallback />}>
                <PayrollRunsPage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="payroll-settings"
          element={
            <RequiresModule code={ModuleCode.PAYROLL}>
              <Suspense fallback={<RouteFallback />}>
                <PayrollSettingsPage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="salary-slips"
          element={
            <RequiresModule code={ModuleCode.PAYROLL}>
              <Suspense fallback={<RouteFallback />}>
                <SalarySlipsPage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="salary-slips/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <SalarySlipDetailPage />
            </Suspense>
          }
        />
        <Route
          path="openings"
          element={
            <RequiresModule code={ModuleCode.RECRUITMENT}>
              <Suspense fallback={<RouteFallback />}>
                <JobOpeningsPage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="openings/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <JobOpeningDetailPage />
            </Suspense>
          }
        />
        <Route
          path="applicants"
          element={
            <RequiresModule code={ModuleCode.RECRUITMENT}>
              <Suspense fallback={<RouteFallback />}>
                <ApplicantPipelinePage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="applicants/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <JobApplicantDetailPage />
            </Suspense>
          }
        />
        <Route
          path="cycles"
          element={
            <RequiresModule code={ModuleCode.PERFORMANCE}>
              <Suspense fallback={<RouteFallback />}>
                <AppraisalCyclesPage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="cycles/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AppraisalCycleDetailPage />
            </Suspense>
          }
        />
        <Route
          path="goals"
          element={
            <RequiresModule code={ModuleCode.PERFORMANCE}>
              <Suspense fallback={<RouteFallback />}>
                <GoalsPage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="reports"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ReportsPage />
            </Suspense>
          }
        />
        <Route
          path="attendance"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AttendancePage />
            </Suspense>
          }
        />
        <Route
          path="calendar"
          element={
            <Suspense fallback={<RouteFallback />}>
              <CalendarPage />
            </Suspense>
          }
        />
        <Route
          path="shifts"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ShiftsPage />
            </Suspense>
          }
        />
        <Route
          path="approvals"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ApprovalsPage />
            </Suspense>
          }
        />
        <Route
          path="ph-compliance"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PhCompliancePage />
            </Suspense>
          }
        />
        <Route
          path="lifecycle"
          element={
            <RequiresModule code={ModuleCode.LIFECYCLE}>
              <Suspense fallback={<RouteFallback />}>
                <LifecyclePage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="lifecycle/separations/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <SeparationDetailPage />
            </Suspense>
          }
        />
        <Route
          path="lifecycle/promotions/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PromotionDetailPage />
            </Suspense>
          }
        />
        <Route
          path="lifecycle/transfers/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <TransferDetailPage />
            </Suspense>
          }
        />
        <Route
          path="lifecycle/grievances/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <GrievanceDetailPage />
            </Suspense>
          }
        />
        <Route
          path="lifecycle/onboardings/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <OnboardingDetailPage />
            </Suspense>
          }
        />
        <Route
          path="lifecycle/final-pays/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <FinalPayDetailPage />
            </Suspense>
          }
        />
        <Route
          path="lifecycle/exit-interviews/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ExitInterviewDetailPage />
            </Suspense>
          }
        />
        <Route
          path="accounting"
          element={
            <RequiresModule code={ModuleCode.ACCOUNTING}>
              <Suspense fallback={<RouteFallback />}>
                <AccountingPage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="accounting/journal-entries/new"
          element={
            <Suspense fallback={<RouteFallback />}>
              <JournalEntryNewPage />
            </Suspense>
          }
        />
        <Route
          path="accounting/journal-entries/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <JournalEntryDetailPage />
            </Suspense>
          }
        />
        <Route
          path="accounting/payment-entries/new"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PaymentEntryNewPage />
            </Suspense>
          }
        />
        <Route
          path="accounting/payment-entries/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PaymentEntryDetailPage />
            </Suspense>
          }
        />
        <Route
          path="accounting/accounts/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AccountDetailPage />
            </Suspense>
          }
        />
        <Route
          path="accounting/fiscal-years/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <FiscalYearDetailPage />
            </Suspense>
          }
        />
        <Route
          path="accounting/cost-centers/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <CostCenterDetailPage />
            </Suspense>
          }
        />
        <Route
          path="accounting/reports"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AccountingReportsPage />
            </Suspense>
          }
        />
        <Route
          path="compensation"
          element={
            <RequiresModule code={ModuleCode.COMPENSATION}>
              <Suspense fallback={<RouteFallback />}>
                <CompensationPage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="compensation/structures/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <SalaryStructureDetailPage />
            </Suspense>
          }
        />
        <Route
          path="workflow-admin"
          element={
            <Suspense fallback={<RouteFallback />}>
              <WorkflowAdminPage />
            </Suspense>
          }
        />
        <Route
          path="workflow-admin/definitions/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <WorkflowDefinitionDetailPage />
            </Suspense>
          }
        />
        <Route
          path="org-settings"
          element={
            <Suspense fallback={<RouteFallback />}>
              <OrgSettingsPage />
            </Suspense>
          }
        />
        <Route
          path="report-builder"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ReportBuilderPage />
            </Suspense>
          }
        />
        <Route
          path="report-builder/queries/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AdHocQueryDetailPage />
            </Suspense>
          }
        />
        <Route
          path="statutory-exports"
          element={
            <Suspense fallback={<RouteFallback />}>
              <StatutoryExportsPage />
            </Suspense>
          }
        />
        <Route
          path="notifications"
          element={
            <Suspense fallback={<RouteFallback />}>
              <NotificationsPage />
            </Suspense>
          }
        />
        <Route
          path="settings"
          element={
            <Suspense fallback={<RouteFallback />}>
              <SettingsPage />
            </Suspense>
          }
        />
        <Route
          path="settings/notifications"
          element={
            <Suspense fallback={<RouteFallback />}>
              <SettingsNotificationsPage />
            </Suspense>
          }
        />
        <Route
          path="settings/profile"
          element={
            <Suspense fallback={<RouteFallback />}>
              <SettingsProfilePage />
            </Suspense>
          }
        />
        <Route
          path="settings/roles"
          element={
            <RequiresRbacManager>
              <Suspense fallback={<RouteFallback />}>
                <SettingsRolesPage />
              </Suspense>
            </RequiresRbacManager>
          }
        />
        <Route
          path="settings/number-series"
          element={
            <Suspense fallback={<RouteFallback />}>
              <SettingsNumberSeriesPage />
            </Suspense>
          }
        />

        {/* Vendor control plane — super-admin only, cross-tenant. */}
        <Route
          path="admin/clients"
          element={
            <RequiresSuperAdmin>
              <Suspense fallback={<RouteFallback />}>
                <AdminClientsPage />
              </Suspense>
            </RequiresSuperAdmin>
          }
        />
        <Route
          path="admin/clients/:id"
          element={
            <RequiresSuperAdmin>
              <Suspense fallback={<RouteFallback />}>
                <AdminClientDetailPage />
              </Suspense>
            </RequiresSuperAdmin>
          }
        />
        <Route
          path="admin/plans"
          element={
            <RequiresSuperAdmin>
              <Suspense fallback={<RouteFallback />}>
                <AdminPlansPage />
              </Suspense>
            </RequiresSuperAdmin>
          }
        />
      </Route>

      {/* Self-service PWA shell — separate layout, ProtectedRoute still applies. */}
      <Route
        path="/me"
        element={
          <ProtectedRoute>
            <MeLayout />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<RouteFallback />}>
              <MeHomePage />
            </Suspense>
          }
        />
        <Route
          path="leave"
          element={
            <Suspense fallback={<RouteFallback />}>
              <MeLeavePage />
            </Suspense>
          }
        />
        <Route
          path="payslips"
          element={
            <Suspense fallback={<RouteFallback />}>
              <MePayslipsPage />
            </Suspense>
          }
        />
        <Route
          path="assistant"
          element={
            <RequiresAiAssistant fallbackTo="/me">
              <Suspense fallback={<RouteFallback />}>
                <MeAssistantPage />
              </Suspense>
            </RequiresAiAssistant>
          }
        />
        <Route
          path="check-in"
          element={
            <RequiresModule
              code={ModuleCode.BIOMETRIC_ATTENDANCE}
              fallbackTo="/me"
            >
              <Suspense fallback={<RouteFallback />}>
                <MeCheckInPage />
              </Suspense>
            </RequiresModule>
          }
        />
        <Route
          path="attendance"
          element={
            <Suspense fallback={<RouteFallback />}>
              <MeAttendancePage />
            </Suspense>
          }
        />
        <Route
          path="profile"
          element={
            <Suspense fallback={<RouteFallback />}>
              <MeProfilePage />
            </Suspense>
          }
        />
      </Route>

      <Route path="404" element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}
